import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import {
  AudioVideoProvider,
  RosterProvider,
  RemoteVideoTileProvider,
  ContentShareProvider,
  NotificationProvider,
  LoggerProvider,
} from "amazon-chime-sdk-component-library-react";
import { AppStateProvider } from "../features/video/providers/AppStateProvider";
import { ErrorProvider } from "../features/video/providers/ErrorProvider";
import { meetingConfig } from "../features/video/meetingConfig.ts";
import { Notifications } from "../features/video/containers/Notifications";
import { MeetingEventObserver } from "../features/video/components/MeetingEventObserver";
import { ChimeFeatureWrappers } from "../features/video/components/ChimeFeatureWrappers";
import { DevicesProvider } from "../features/video/providers/DevicesProvider";
import { VideoProvider } from "../features/video/providers/VideoContext";
import { VideoTileGridProvider } from "@platform/features/video/providers/VideoTileGridProvider/index.tsx";
import { VideoUIDialogs } from "../features/videoUI/components/VideoUIDialogs/VideoUIDialogs";
import { TrackingProvider } from "@core/features/tracking";
import { TranscriptionProvider } from "../features/video/providers/TranscriptionProvider";
import { VideoEventListeners } from "../features/video/VideoEventListeners";
import { ChatSessionContainer } from "@platform/features/chat";
import {
  stopAllTrackedStreams,
  forceStopAllMediaStreams,
} from "../features/video/utils/stopChimeStreams";
import { DashboardHeaderContainer } from "@platform/layouts/DashboardHeader";
import { DashboardNavigationItemsContainer } from "@platform/layouts/DashboardNavigation";
import { DashboardAlertBannerContainer } from "@platform/layouts/DashboardAlertBanner";
import { ChatSheetContainer } from "@platform/features/chat";
import { usePlatformStore } from "@platform/store";

export type Props = {
  isWithNavigation?: boolean;
};

export function VideoLayout({ isWithNavigation = false }: Props) {
  const isTranscriptionDisabled = usePlatformStore(
    (s) => s.featureFlags.isTranscriptionDisabled
  );

  // Clean up media streams when navigating away from video pages
  useEffect(() => {
    return () => {
      // Clean up when component unmounts (navigating away)
      stopAllTrackedStreams();
      forceStopAllMediaStreams();
    };
  }, []);

  // TODO: Fetch user data for video pages

  return (
    <TrackingProvider
      debug={{ enabled: false }}
      services={{ posthog: true, google_analytics: true }}
    >
      <LoggerProvider logger={meetingConfig.logger}>
        <AppStateProvider>
          <ChimeFeatureWrappers>
            <ErrorProvider>
              <NotificationProvider>
                <AudioVideoProvider>
                  <DevicesProvider>
                    <VideoProvider>
                      <VideoTileGridProvider>
                        <RosterProvider>
                          <TranscriptionProvider
                            isTranscriptionDisabled={
                              isTranscriptionDisabled || false
                            }
                          >
                            <RemoteVideoTileProvider>
                              <ContentShareProvider>
                                <ChatSessionContainer>
                                  <Notifications />
                                  <MeetingEventObserver />
                                  <VideoEventListeners />
                                  {isWithNavigation ? (
                                    <div className="min-h-[100dvh] flex flex-col bg-brand-50 text-brand-800">
                                      <DashboardHeaderContainer>
                                        <DashboardNavigationItemsContainer />
                                      </DashboardHeaderContainer>
                                      <DashboardAlertBannerContainer />
                                      <main className="flex flex-1 h-full justify-center items-center">
                                        <Outlet />
                                      </main>
                                    </div>
                                  ) : (
                                    <main className="flex-grow flex flex-col bg-brand-50 h-full min-h-[100dvh]">
                                      <Outlet />
                                    </main>
                                  )}
                                  <VideoUIDialogs />
                                  <ChatSheetContainer />
                                </ChatSessionContainer>
                              </ContentShareProvider>
                            </RemoteVideoTileProvider>
                          </TranscriptionProvider>
                        </RosterProvider>
                      </VideoTileGridProvider>
                    </VideoProvider>
                  </DevicesProvider>
                </AudioVideoProvider>
              </NotificationProvider>
            </ErrorProvider>
          </ChimeFeatureWrappers>
        </AppStateProvider>
      </LoggerProvider>
    </TrackingProvider>
  );
}

export default VideoLayout;
